import { Page } from '@/components/dashboard/page';
import { createFileRoute } from '@tanstack/react-router';
import { Heading, Icon, Para } from 'ht-sdk/react';
import { useSession } from 'ht-sdk/auth/client';
import { auth } from '@/auth';
import { ChevronRight, ClipboardList, Globe, Settings, SquareChartGantt, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import { api } from '@/api';
import { LocationTable } from '@/routes/dashboard/locations';
import { useEffect, useState } from 'react';
import type { Location } from '@shared/modelTypes/location-types';

export const Route = createFileRoute('/dashboard/')({
	component: RouteComponent,
	loader: async () => {
		const data = await api.dashboardData({});
		const result = data.success
			? { ...data.data }
			: {
					locations: [] as Location[],
					locationCount: 0,
					locationIncrease: 0,
					sessionCount: 0,
					sessionIncrease: 0,
					roomCount: 0,
				};

		return { ...result };
	},
});

function RouteComponent() {
	const data = Route.useLoaderData();
	const [locations, setLocations] = useState<Location[]>(data.locations);

	const session = useSession(auth);
	const isAdmin = session?.role == 'admin';

	return (
		<Page>
			<div className="gap-1">
				<Heading>
					<span className="font-extrabold">Welcome {session?.name}</span>
				</Heading>
				<Para className="mb-4">{getRandomWelcomeMessage()}</Para>

				<div className="mt-2 mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4">
					<DashboardButton icon={Globe} label="Locations" href="/dashboard/locations" />
					{isAdmin && <DashboardButton icon={ClipboardList} label="Rooms" href="/dashboard/rooms" />}
					{isAdmin && <DashboardButton icon={Settings} label="Settings" href="/dashboard/settings" />}
				</div>

				<Heading mode="tertiary">
					<span className="font-extrabold">Recent locations:</span>
				</Heading>

				<LocationTable locations={locations} setLocations={setLocations} isAdmin={isAdmin} />

				{/* <RoomUnlockAlgoTest /> */}
			</div>

			<Heading mode="tertiary">
				<span className="font-extrabold">Statistics:</span>
			</Heading>

			<div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4 md:grid-cols-5">
				<Stat icon={Globe} data={data.locationCount} label={'locations'} increase={data.locationIncrease} />
				<Stat icon={SquareChartGantt} data={data.sessionCount} label={'sessions'} increase={data.sessionIncrease} />
				<Stat icon={ClipboardList} data={data.roomCount} label={'rooms'} />
			</div>
		</Page>
	);
}

function getRandomWelcomeMessage() {
	const welcomeMessages = [
		'Nice to see you again!',
		'Welcome back!',
		"Glad you're here!",
		'Great to have you again!',
		"You're looking sharp today!",
		"Hope you're having a great day!",
	];

	const index = Math.floor(Math.random() * welcomeMessages.length);
	return welcomeMessages[index];
}

type DashboardButtonProps = {
	icon: string | LucideIcon;
	label: string;
	href: string;
};

function DashboardButton({ icon, label, href }: DashboardButtonProps) {
	return (
		<a href={href}>
			<div
				className={`group flex cursor-pointer items-center gap-4 rounded-2xl bg-white p-4 shadow transition-colors duration-200 hover:bg-gradient-to-br hover:from-[var(--color-theme-500)] hover:to-[var(--color-theme-800)] hover:text-white`}
			>
				<div className={`group-hover:bg-theme-700 rounded-full bg-gray-100 p-3 transition-colors duration-200 dark:bg-gray-700`}>
					<Icon icon={icon} className={`group-hover:text-white`} />
				</div>
				<div>
					<p className="flex text-sm text-gray-500 transition-colors duration-200 group-hover:text-white dark:text-gray-400">
						{label}
						<ChevronRight className="mb-1 size-5" />
					</p>
				</div>
			</div>
		</a>
	);
}

const CountUp = ({ target }: { target: number }) => {
	const [count, setCount] = useState(0);

	useEffect(() => {
		let start = 0;
		const step = Math.ceil(target / 40);
		const interval = setInterval(() => {
			start += step;
			if (start >= target) {
				setCount(target);
				clearInterval(interval);
			} else {
				setCount(start);
			}
		}, 30);
		return () => clearInterval(interval);
	}, [target]);

	return <span>{count}</span>;
};

type StatProps = {
	icon: string | LucideIcon;
	data: number;
	label: string;
	increase?: number | undefined;
};

function Stat({ icon, data, label, increase }: StatProps) {
	return (
		<div className="flex flex-col items-center gap-4 rounded-2xl bg-white p-4 shadow">
			<div className="rounded-full bg-gray-100 p-3 transition-colors duration-200">
				<Icon icon={icon} className="group-hover:text-white" />
			</div>
			<div className="text-center">
				<p className="text-sm text-gray-500 transition-colors duration-200">
					<CountUp target={data} /> {label}
				</p>
				{increase !== 0 && increase && (
					<p
						className={`flex gap-1 text-xs transition-colors duration-200 ${increase > 0 ? 'text-green-500' : 'text-danger-500'}`}
					>
						{increase > 0 ? <TrendingUp className="size-4" /> : <TrendingDown className="size-4" />}
						<span>
							<CountUp target={increase} /> last 7 days
						</span>
					</p>
				)}
			</div>
		</div>
	);
}

// function RoomUnlockAlgoTest() {
// 	const form = useForm<StressIndicator>({
// 		mode: 'onChange',
// 		defaultValues: {
// 			techTeamCount: 2,
// 			timeProgress: 0.5,
// 			openRoomCount: 1,
// 			occupiedRoomCount: 1,
// 			roomAlmostFinishedCount: 1,
// 			systemDamage: 0.3,
// 		},
// 	});

// 	const stressIndicator = form.watch();
// 	const result = chooseRoomsCountToOpen(stressIndicator);
// 	return (
// 		<div>
// 			<Heading>Room Unlock Algo Test</Heading>
// 			<Form methods={form} onSubmit={() => {}}>
// 				<NumberField name="techTeamCount" label="Tech Team Count" />
// 				<NumberField name="timeProgress" label="Time Progress" />
// 				<NumberField name="openRoomCount" label="Open Room Count" />
// 				<NumberField name="occupiedRoomCount" label="Occupied Room Count" />
// 				<NumberField name="roomAlmostFinishedCount" label="Room Almost Finished Count" />
// 				<NumberField name="systemDamage" label="System Damage" />
// 			</Form>
// 			<Code block>{JSON.stringify(result, null, 2)}</Code>
// 		</div>
// 	);
// }
